import { useEffect, useRef, useState } from "react";
import { createClient } from "@supabase/supabase-js";
import { Form } from "@/components/ui/form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { useAuthStep } from "@/hooks/useAuthStep";

import DOB from "@/components/DOB";
import { SUPABASE_ANON_KEY, SUPABASE_URL } from "@/lib/config";
import { getAgeBand, trackEvent } from "@/lib/utils";


const supabase = createClient(SUPABASE_URL!, SUPABASE_ANON_KEY!);

const formSchema = z.object({
    month: z.string().min(1, "Please select a month"),
    year: z.string().min(1, "Please select a year"),
});

const EmailAuth = ({ method }: { method: string }) => {

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);

    const [name, setName] = useState("");

    const [loading, setLoading] = useState(false);
    const { setAuthStep, setToken, setEmailLogin, uuid, setAgeBand } = useAuthStep();


    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            month: "",
            year: "",
        },
    });

    const handleSignUp = async () => {
        setLoading(true);
        let data, error;

        // Use magic link for both signup and login
        const res = await supabase.auth.signInWithOtp({
            email,
            options: {
                data: {
                  full_name: name, // Pass the name here
                },
              },
        });
        data = res.data;
        error = res.error;

        if (error) {
            console.error(error);
            setLoading(false);
        } else {
            console.log("Magic link sent to:", email);
            // Show success message instead of immediate redirect
            setAuthStep("magicLinkSent");

        }
    }

    const isDOBValid = () => {
        const formData = form.getValues();
        const month = formData.month;
        const year = formData.year;

        // Check if both month and year are selected
        if (!month || !year) {
            return false;
        }
        return true;
    };

    useEffect(() => {
        const subscription = form.watch((value) => {
            setIsButtonDisabled(!isDOBValid());
        });
        return () => subscription.unsubscribe();
    }, [form]);

    const checkAgeRef = useRef<HTMLDivElement>(null);

    const checkAge = (e: any) => {

        e.preventDefault();
        console.log("checkAge", isButtonDisabled);

        if (isButtonDisabled) return;

        const { year, month } = form.getValues();

        if (new Date().getFullYear() - parseInt(year) < 13 || (new Date().getFullYear() - parseInt(year) === 13 && new Date().getMonth() + 1 < parseInt(month))) {
            window?.top?.postMessage({
                type: "AUTH_UNDER_AGE",
            }, "*");
            setAuthStep("underAge");
            return;
        } else {
            setAgeBand(getAgeBand(parseInt(year)));
            setEmailLogin(email);
            handleSignUp();
        }

    }

    useEffect(() => {
        trackEvent("signup_email_loaded", { "distinct_id": uuid });
    }, []);

    return (
        <div className="px-[30px] flex flex-col gap-[10px]">
            <h1 className="text-2xl font-semibold mb-[5px]">Join now (takes 30 secs)</h1>
            <p className="text-gray-500">Over 1.5 million students made their Canvas better.</p>

            <Form {...form}>
                <form>
                    <DOB form={form} />
                    <div className={`mt-2 ${isButtonDisabled ? "opacity-50 pointer-events-none" : ""}`}>
                        <p className="text-black text-base font-medium">Email</p>
                        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className="w-full rounded-lg shadow-xs text-gray-500 text-sm p-[10px] border border-gray-200" />
                        <input
                            className="mt-2 w-full rounded-lg shadow-xs text-gray-500 text-sm p-[10px] border border-gray-200"
                            type="email"
                            placeholder="lebronjames@gmail.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    checkAge(e);
                                }
                            }}
                        />
                        <button onClick={checkAge} className={`mt-2 w-full rounded-full shadow-xs text-white text-sm p-[10px] border border-gray-200 ${email.includes("@") ? "opacity-100" : "opacity-50 pointer-events-none"} `} style={{ "background": "linear-gradient(120deg, var(--brand-1), var(--brand-2))" }}>Sign up</button>
                    </div>

                </form>
            </Form>

            <div className="text-[11px] text-gray-500 mt-2 flex-1">
                By signing up, you acknowledge that you have read, understood, and agree to the BetterCampus <a href="/terms" target="_blank" className="text-blue-500">Terms</a> and <a href="/privacy" target="_blank" className="text-blue-500">Privacy Policy</a>
            </div>


        </div>
    )
}

export default EmailAuth;